import * as React from 'react'

import { deriveContentState } from './deriveContentState'
import { type GlobalSearchRecentItem } from '../../../../value-objects/GlobalSearchRecentItem'
import { type ContentStateEnum } from '../../enums/ContentStateEnum'
import { useMutateAddGlobalSearchRecentItem } from '../../models/useMutateAddGlobalSearchRecentItem'
import { useQueryGlobalSearchRecentItems } from '../../models/useQueryGlobalSearchRecentItems'
import { useQueryGlobalSearchResults } from '../../models/useQueryGlobalSearchResults'

interface IUseGetGlobalSearchResultsPresenterResult {
  commands: {
    onQueryChange: (value: string) => void
    onSubmit: () => void
    onRecentItemClick: (item: GlobalSearchRecentItem) => void
  }
  data: {
    query: string
    recentItems: GlobalSearchRecentItem[]
    inputRef: React.RefObject<HTMLInputElement>
  }
  states: {
    showEnterHint: boolean
    contentState: Nullable<ContentStateEnum>
  }
}

export const useGetGlobalSearchResultsPresenter =
  (): IUseGetGlobalSearchResultsPresenterResult => {
    const [query, setQuery] = React.useState('')
    const [submittedQuery, setSubmittedQuery] = React.useState('')
    const inputRef = React.useRef<HTMLInputElement>(null)

    const { recentItems } = useQueryGlobalSearchRecentItems()
    const { addRecentItem } = useMutateAddGlobalSearchRecentItem()
    const { isLoading: isSearchLoading } =
      useQueryGlobalSearchResults(submittedQuery)

    const showEnterHint = query !== submittedQuery && query.length > 0

    const contentState: Nullable<ContentStateEnum> = deriveContentState({
      submittedQuery,
      hasRecentItems: recentItems.length > 0,
      isSearchLoading,
    })

    const onQueryChange = React.useCallback((value: string) => {
      setQuery(value)
      if (value === '') {
        setSubmittedQuery('')
      }
    }, [])

    const onSubmit = React.useCallback(() => {
      setSubmittedQuery((prev) => (prev === query ? prev : query))
    }, [query])

    const onRecentItemClick = React.useCallback(
      (item: GlobalSearchRecentItem) => {
        addRecentItem({
          type: item.type,
          uuid: item.uuid,
          id: item.id,
          metadata: item.metadata,
        })
      },
      [addRecentItem]
    )

    return {
      commands: {
        onQueryChange,
        onSubmit,
        onRecentItemClick,
      },
      data: {
        query,
        recentItems,
        inputRef,
      },
      states: {
        showEnterHint,
        contentState,
      },
    }
  }
